import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Check,
  CircleDashed,
  ChevronDown,
  ChevronRight,
  Ellipsis,
  FlaskConical,
  MessageSquare,
  Pill,
} from 'lucide-react';

import type {
  ClinicianSuggestionViewT,
  ProposedClinicalActionResultT,
  ProposedClinicalActionT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { lotusDarkLogo } from '@/shared/assets';
import { useCareStateDomain } from '@/entities/care-state-domain';
import { reportError } from '@/shared/lib';

import { fetchPatientSuggestions } from '../api/fetchPatientSuggestions';
import { approvePatientSuggestion, dismissPatientSuggestion } from '../api/decidePatientSuggestion';
import { groupSuggestionsByProblem } from '../model/groupSuggestionsByProblem';
import { SuggestionChat } from './SuggestionChat';

interface PatientSuggestionsSectionProps {
  userId: string;
}

const EMPTY_SUGGESTIONS: ClinicianSuggestionViewT[] = [];

type ProposalDisplay = { label: string; icon: typeof Pill };
type SuggestionCardProps = { suggestion: ClinicianSuggestionViewT; userId: string };

function proposalLabel(proposal: ProposedClinicalActionT['proposal']): ProposalDisplay | null {
  switch (proposal.type) {
    case 'request_prescription':
      return { label: 'Prescription', icon: Pill };
    case 'request_lab':
      return { label: 'Lab request', icon: FlaskConical };
    case 'discuss':
      return { label: 'Discuss', icon: MessageSquare };
    default:
      return null;
  }
}

function ProposedAction({
  action: result,
  selected,
  onToggle,
}: {
  action: ProposedClinicalActionResultT;
  selected: boolean;
  onToggle: () => void;
}) {
  // The server marks an action it could not read as unavailable.
  if (!('proposal' in result)) {
    return <UnavailableProposedAction />;
  }

  const display = proposalLabel(result.proposal);
  if (!display) {
    return <UnavailableProposedAction />;
  }

  return (
    <SelectableProposedAction
      action={result}
      display={display}
      selected={selected}
      onToggle={onToggle}
    />
  );
}

function UnavailableProposedAction() {
  return (
    <li className="group flex gap-3 rounded-xl px-4 py-2">
      <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground">
        <CircleDashed className="size-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="py-1 text-[13px] text-muted-foreground">Proposed action unavailable</div>
      </div>
    </li>
  );
}

function SelectableProposedAction({
  action,
  display,
  selected,
  onToggle,
}: {
  action: ProposedClinicalActionT;
  display: ProposalDisplay;
  selected: boolean;
  onToggle: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      className={`group flex cursor-pointer gap-3 rounded-xl px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? 'border border-success-border bg-success-muted hover:bg-success-muted' : 'hover:bg-muted/40'}`}
    >
      <span
        className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full ${selected ? 'bg-success text-success-foreground' : 'text-muted-foreground'}`}
      >
        {selected ? (
          <Check className="size-3" strokeWidth={3} aria-hidden="true" />
        ) : (
          <CircleDashed className="size-4" aria-hidden="true" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <p className="min-w-0 flex-1 text-[13px] font-normal leading-snug">
            {action.proposal.subtitle}
          </p>
          <div className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
            <button
              type="button"
              aria-expanded={expanded}
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((value) => !value);
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Why{' '}
              {expanded ? (
                <ChevronDown className="size-3.5" />
              ) : (
                <ChevronRight className="size-3.5" />
              )}
            </button>
            <span aria-hidden="true">·</span>
            <display.icon className="size-3.5" aria-hidden="true" />
            <span>{display.label}</span>
          </div>
        </div>
        <p className="mt-1 text-[11px] leading-[1.55] text-foreground/70">
          {action.proposal.instruction}
        </p>
        {expanded && (
          <div className="mt-2 flex gap-3 rounded-lg bg-muted/70 px-3 py-2.5 text-[11px] leading-relaxed text-foreground/75">
            <img
              src={lotusDarkLogo}
              alt=""
              className="mt-0.5 size-4 shrink-0 object-contain invert dark:invert-0"
              aria-hidden="true"
            />
            <p>{action.rationale}</p>
          </div>
        )}
      </div>
    </li>
  );
}

function SuggestionCard({ suggestion, userId }: SuggestionCardProps) {
  const [contextExpanded, setContextExpanded] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [decisionPending, setDecisionPending] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const suggestionsQueryKey = ['/greenfield/care', userId, 'suggestions'];
  useEffect(() => setSelectedIds([]), [suggestion.revision]);
  if (!('title' in suggestion.draft)) {
    return (
      <article className="p-4">
        <p className="font-medium">Suggestion details unavailable</p>
        <p className="mt-1 text-sm text-muted-foreground">
          This suggestion could not be safely loaded.
        </p>
      </article>
    );
  }
  const draft = suggestion.draft;
  const selectedActions = draft.proposed_clinical_actions
    .filter(
      (action): action is ProposedClinicalActionT =>
        'id' in action && selectedIds.includes(action.id)
    )
    .map(({ id, proposal }) => ({ id, title: proposal.subtitle }));

  const refreshSuggestions = () => queryClient.invalidateQueries({ queryKey: suggestionsQueryKey });

  const approve = async () => {
    const plan = draft.proposed_clinical_actions.filter(
      (action): action is ProposedClinicalActionT =>
        'id' in action && selectedIds.includes(action.id)
    );
    if (plan.length === 0) {
      return;
    }

    setDecisionPending(true);
    setDecisionError(null);
    try {
      const { outcome } = await approvePatientSuggestion(userId, suggestion.actionId, {
        expectedRevision: suggestion.revision,
        plan,
      });
      if (outcome.type === 'approved') {
        await refreshSuggestions();
      } else if (outcome.type === 'refused') {
        if (outcome.reason === 'stale_revision') {
          setSelectedIds([]);
          await refreshSuggestions();
        }
        setDecisionError(outcome.details);
      } else {
        await refreshSuggestions();
      }
    } catch (error) {
      setDecisionError(
        error instanceof Error ? error.message : 'Could not approve this suggestion.'
      );
    } finally {
      setDecisionPending(false);
    }
  };

  const dismiss = async () => {
    setDecisionPending(true);
    setDecisionError(null);
    try {
      const { outcome } = await dismissPatientSuggestion(userId, suggestion.actionId);
      if (outcome.type === 'dismissed' || outcome.type === 'not_a_suggestion') {
        await refreshSuggestions();
      } else {
        setDecisionError(outcome.details);
        await refreshSuggestions();
      }
    } catch (error) {
      setDecisionError(error instanceof Error ? error.message : 'Could not close this suggestion.');
    } finally {
      setDecisionPending(false);
    }
  };

  return (
    <article className="overflow-hidden">
      <div className="grid gap-3 px-4 pb-4 pt-2 lg:grid-cols-[minmax(0,1.6fr)_minmax(18rem,1fr)]">
        <section className="min-w-0">
          <h4 className="text-[13px] font-medium leading-snug">{draft.title}</h4>
          <ul className="mt-3 space-y-1">
            {draft.proposed_clinical_actions.map((action, index) => (
              <ProposedAction
                key={'id' in action ? action.id : `unavailable-${index}`}
                action={action}
                selected={'id' in action && selectedIds.includes(action.id)}
                onToggle={() => {
                  if (!('id' in action) || decisionPending) {
                    return;
                  }
                  setSelectedIds((ids) =>
                    ids.includes(action.id)
                      ? ids.filter((id) => id !== action.id)
                      : [...ids, action.id]
                  );
                }}
              />
            ))}
          </ul>
          <div className="mt-4 flex items-center justify-between gap-4">
            <button
              type="button"
              disabled={decisionPending}
              onClick={dismiss}
              className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            >
              <CheckCircle2 className="size-4" /> Close without action
            </button>
            <button
              type="button"
              disabled={selectedIds.length === 0 || decisionPending}
              onClick={approve}
              className={`inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] font-medium ${selectedIds.length ? 'bg-success text-success-foreground hover:bg-success/90' : 'bg-muted text-muted-foreground opacity-60'}`}
            >
              {selectedIds.length > 0 && (
                <Check className="size-4" strokeWidth={2.5} aria-hidden="true" />
              )}
              Approve{selectedIds.length ? ` ${selectedIds.length} and close` : ''}
            </button>
          </div>
          {decisionError && (
            <p role="alert" className="mt-2 text-xs text-destructive">
              {decisionError}
            </p>
          )}
        </section>
        <aside className="rounded-lg border border-border bg-card p-3">
          <header className="flex items-center justify-between gap-3">
            <h4 className="flex items-center gap-2 text-xs font-medium">
              <img
                src={lotusDarkLogo}
                alt=""
                className="size-4 object-contain invert dark:invert-0"
              />{' '}
              Clinical context
            </h4>
            <button
              type="button"
              disabled
              aria-label="More context options"
              className="text-muted-foreground opacity-50"
            >
              <Ellipsis className="size-5" />
            </button>
          </header>
          <div className={`relative mt-3 ${contextExpanded ? '' : 'max-h-48 overflow-hidden'}`}>
            <p className="whitespace-pre-wrap text-[12px] leading-[1.6] text-foreground/75">
              {draft.cortex_context}
            </p>
            {!contextExpanded && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent"
              />
            )}
          </div>
          <button
            type="button"
            aria-expanded={contextExpanded}
            onClick={() => setContextExpanded((expanded) => !expanded)}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {contextExpanded ? 'Show less' : 'Show full context'}
            {contextExpanded ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
          </button>
        </aside>
      </div>
      <div className="border-t bg-muted/10 px-5 py-4 lg:px-7">
        <SuggestionChat
          userId={userId}
          actionId={suggestion.actionId}
          draftFingerprint={JSON.stringify(draft)}
          selectedActions={selectedActions}
          onRemoveSelected={(id) =>
            setSelectedIds((ids) => ids.filter((selectedId) => selectedId !== id))
          }
        />
      </div>
    </article>
  );
}

function UnplacedSuggestionCard({ suggestion, userId }: SuggestionCardProps) {
  return (
    <section className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <h3 className="px-4 pt-3 text-sm font-medium leading-snug">Suggestion problem unavailable</h3>
      <SuggestionCard suggestion={suggestion} userId={userId} />
    </section>
  );
}

export function PatientSuggestionsSection({ userId }: PatientSuggestionsSectionProps) {
  const { careState, status: careStateStatus } = useCareStateDomain();
  const query = useQuery({
    queryKey: ['/greenfield/care', userId, 'suggestions'],
    queryFn: ({ signal }) => fetchPatientSuggestions(userId, signal),
    enabled: userId.length > 0,
  });
  const suggestions = (query.data ?? EMPTY_SUGGESTIONS).filter(
    (suggestion) => suggestion.lifecycle.type === 'open'
  );
  const grouping = useMemo(
    () => groupSuggestionsByProblem(suggestions, careState?.problems ?? []),
    [careState?.problems, suggestions]
  );

  useEffect(() => {
    if (careStateStatus === 'loading') {
      return;
    }
    for (const suggestion of grouping.ungrouped) {
      reportError(new Error('Suggestion problem placement could not be resolved'), {
        surface: 'handled',
        feature: 'patient-suggestions',
        operation: 'group-by-problem',
        extra: {
          patientUserId: userId,
          suggestionActionId: suggestion.actionId,
          careStateProblemId: suggestion.placement?.care_state_problem_id,
        },
      });
    }
  }, [careStateStatus, grouping.ungrouped, userId]);

  if (query.isPending) {
    return <div className="p-6 text-sm text-muted-foreground">Loading suggestions…</div>;
  }
  if (query.isError) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Suggestions could not be loaded. Try again later.
      </div>
    );
  }
  return (
    <section aria-label="Unresolved suggestions" className="flex flex-col gap-3">
      {suggestions.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
          No open suggestions for this patient.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {grouping.groups.map((group) => (
            <section
              key={group.key}
              className="overflow-hidden rounded-xl border bg-card shadow-sm"
            >
              <h3 className="px-4 pt-3 text-sm font-medium leading-snug">{group.label}</h3>
              <div className="divide-y">
                {group.suggestions.map((suggestion) => (
                  <SuggestionCard
                    key={suggestion.actionId}
                    suggestion={suggestion}
                    userId={userId}
                  />
                ))}
              </div>
            </section>
          ))}
          {grouping.ungrouped.map((suggestion) => (
            <UnplacedSuggestionCard
              key={suggestion.actionId}
              suggestion={suggestion}
              userId={userId}
            />
          ))}
        </div>
      )}
    </section>
  );
}
